import { useMemo } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import type { RollsetListItem } from "../models/RollshopOperations";
import {
	getAssignmentStatus,
	OP_STATUS_IN_USE,
	OP_STATUS_PREPARED_FOR_LINE,
	type PassStatusOption,
	type RollsetStatusChange,
	type RollsProductionRow,
} from "../models/Rollshop";
import { PASS_CONDITION_HEIGHT, PassConditionGrid } from "./PassConditionGrid";
import { RollsetInfoSection, RollsSection } from "./RollsetSections";
import { SetOpStatusButton } from "./SetOpStatusButton";
import {
	OPERATIONAL_WRITES_LOCKED_MESSAGE,
	useOperationalWritesLocked,
} from "@/shared/hooks/useOperationalWritesLocked";

interface Props {
	rollset: RollsetListItem | null;
	production: RollsProductionRow[];
	isLoadingProduction: boolean;
	passStatus: PassStatusOption[];
	statusChanges: RollsetStatusChange[];
	isSettingStatus: boolean;
	onTurn: () => void;
	onSetStatus: (statusCode: string) => void;
	onEditPass: (row: RollsProductionRow) => void;
	onClose: () => void;
	onStatusMenuOpenChange: (open: boolean) => void;
}

export const RollshopDetailPanel = ({
	rollset,
	production,
	isLoadingProduction,
	passStatus,
	statusChanges,
	isSettingStatus,
	onTurn,
	onSetStatus,
	onEditPass,
	onClose,
	onStatusMenuOpenChange,
}: Props) => {
	const writesLocked = useOperationalWritesLocked();
	const lockedReason = writesLocked ? OPERATIONAL_WRITES_LOCKED_MESSAGE : "";
	// Legacy: Prepared for line and In use are only set from the mill setup flows.
	const targets = useMemo(
		() =>
			statusChanges.filter(
				(c) =>
					c.FromCode === rollset?.StatusCode &&
					c.ToCode !== OP_STATUS_IN_USE &&
					c.ToCode !== OP_STATUS_PREPARED_FOR_LINE,
			),
		[statusChanges, rollset?.StatusCode],
	);

	if (!rollset) {
		return (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography color="text.secondary">
					Select a rollset to see its rolls and pass condition.
				</Typography>
			</Box>
		);
	}

	const assignment = getAssignmentStatus(rollset);
	const isAssigned = assignment === "ASSIGNED";
	const isInUse = rollset.StatusCode === OP_STATUS_IN_USE;
	const isPrepared = rollset.StatusCode === OP_STATUS_PREPARED_FOR_LINE;

	const turnBlockedReason = writesLocked
		? lockedReason
		: !isAssigned
			? "The rollset must have all its rolls assigned."
			: isInUse
				? "The rollset is In use."
				: "";
	const statusBlockedReason = writesLocked
		? lockedReason
		: !isAssigned
			? "The rollset must have all its rolls assigned."
			: isInUse || isPrepared
				? `The rollset is ${rollset.StatusName}. Its Op. Status changes from Mill Setup or Rolls Cartridge.`
				: targets.length === 0
					? `There is no manual Op. Status change from ${rollset.StatusName}.`
					: "";

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<DetailHeader
				title={rollset.Name}
				onClose={onClose}
				isSaving={isSettingStatus}
				actions={
					<>
						<SetOpStatusButton
							targets={targets}
							disabled={statusBlockedReason !== ""}
							disabledReason={statusBlockedReason}
							isSaving={isSettingStatus}
							onConfirm={onSetStatus}
							onOpenChange={onStatusMenuOpenChange}
						/>
						<Tooltip title={turnBlockedReason}>
							<span>
								<Button
									size="small"
									variant="contained"
									disabled={turnBlockedReason !== ""}
									onClick={onTurn}
								>
									Turn
								</Button>
							</span>
						</Tooltip>
					</>
				}
			/>
			<DetailBody>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						gap: 0.75,
					}}
				>
					<RollsetInfoSection rollset={rollset} />
					<RollsSection rollset={rollset} />
					<Box
						sx={{
							flex: "0 1 auto",
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
						}}
					>
						<DetailSection
							title="Pass Condition"
							icon={<FormatListNumberedIcon />}
							fill
						>
							<Box
								sx={{
									flex: "0 1 auto",
									height: PASS_CONDITION_HEIGHT,
									minHeight: 0,
								}}
							>
								{!isAssigned ? (
									<Typography color="text.secondary" sx={{ p: 1 }}>
										The rollset must have all its rolls assigned.
									</Typography>
								) : isLoadingProduction && production.length === 0 ? (
									<CenteredLazyLoading />
								) : (
									<PassConditionGrid
										rows={production}
										passStatus={passStatus}
										editable={!isInUse}
										lockedReason={lockedReason}
										onEdit={onEditPass}
									/>
								)}
							</Box>
						</DetailSection>
					</Box>
				</Box>
			</DetailBody>
		</Box>
	);
};
